import * as React from "react";

import { ChatHub } from "@/components/chat/chat-hub";
import { AdminNavbar } from "@/components/layout/AdminNavbar";
import { AdminSidebar } from "@/components/layout/AdminSidebar";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { SidebarInset, SidebarProvider } from "@nt/ui/sidebar";
import { cn } from "@/lib/utils";
import type { Me } from "@/types";

const SIDEBAR_STATE_COOKIE = /(?:^|;\s*)sidebar_state=(true|false)/;

// Restore the persisted collapse state written by the shadcn SidebarProvider
// (cookie `sidebar_state`). Runs client-side only; the gate renders skeletons
// until the session resolves, so the sidebar subtree never appears before the
// cookie can be read.
function readSidebarDefaultOpen(): boolean {
  if (typeof document === "undefined") return true;
  const match = document.cookie.match(SIDEBAR_STATE_COOKIE);
  return match ? match[1] === "true" : true;
}

function hasStickyHeaderProp(
  child: React.ReactNode,
): child is React.ReactElement {
  return (
    React.isValidElement(child) &&
    (child.props as Record<string, unknown>)?.["data-sticky-header"] === true
  );
}

/**
 * The console shell on the shadcn inset sidebar, mirroring the old admin's
 * dashboard shell.
 *
 * Scroll model: the provider + inset are height-locked (`h-svh
 * overflow-hidden`) so the page body never scrolls; the mobile navbar stays
 * fixed and page content scrolls inside the white inset card with a hidden
 * inner scrollbar. Because the inset is height-bounded, a page's own
 * `PageWrapper` (`flex h-full flex-col` → `shrink-0` header + `flex-1
 * overflow-y-auto` body) pins its header.
 */
export function AdminShell({
  me,
  children,
}: {
  me: Me | null;
  children: React.ReactNode;
}) {
  const [defaultOpen] = React.useState(readSidebarDefaultOpen);

  const childArray = React.Children.toArray(children);
  const stickyHeader = childArray.find(hasStickyHeaderProp);
  const bodyChildren = stickyHeader
    ? childArray.filter((child) => !hasStickyHeaderProp(child))
    : children;

  return (
    <SidebarProvider
      defaultOpen={defaultOpen}
      className="h-svh overflow-hidden"
    >
      <AdminSidebar me={me} />
      {/* One instance for the whole console. Renders nothing until Cmd+K, so
          it costs a closed state and a keydown listener. */}
      <CommandPalette />
      {/* The live chat hub: browser sessions only, on every page, renders nothing. */}
      {me?.agent === "session" ? <ChatHub meId={me.authUserId} /> : null}
      {/* The inset default (paper) rather than `tone="card"`: the design system
          is white cards lifting off warm paper, so the content pane is the
          paper and every section on it is a card. The sidebar rail is the
          quieter surface tone around it. */}
      <SidebarInset className="h-svh overflow-hidden md:peer-data-[variant=inset]:h-[calc(100svh-1rem)]">
        <AdminNavbar />
        {stickyHeader && (
          <div className="sticky top-0 z-40 shrink-0 border-b border-border bg-background/95 backdrop-blur-md">
            <div className="px-4 py-4 sm:px-6 lg:px-8 lg:py-6">
              {stickyHeader}
            </div>
          </div>
        )}
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            stickyHeader && "px-4 py-4 sm:px-6 lg:px-8 lg:pt-6",
          )}
        >
          {stickyHeader ? (
            <div className="w-full max-w-none">{bodyChildren}</div>
          ) : (
            bodyChildren
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
